import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  TablePagination,
  Stack,
} from '@mui/material';
import {
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AssignmentInd as AssignmentIndIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../utils/config';

interface VendorAssociation {
  id: string;
  displayName: string;
  companyName?: string;
  active: boolean;
  linkedUsers: Array<{
    id: string;
    name: string;
    email: string;
  }>;
  createdTime: string;
  lastUpdatedTime: string;
  balance?: number;
  currencyCode?: string;
  contact?: {
    phone?: string;
    email?: string;
    website?: string;
  };
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface QuickBooksVendor {
  id: string;
  displayName: string;
  companyName?: string;
  active: boolean;
}

interface QueueItem {
  id: number;
  vendorId: string;
  vendorName: string;
  vendorCompany?: string;
  userEmail: string;
  complete: boolean;
  createdAt: string;
}

export const AccountingUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [vendors, setVendors] = useState<VendorAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorAssociation | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [quickBooksVendors, setQuickBooksVendors] = useState<QuickBooksVendor[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Queue state
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [openQueueDialog, setOpenQueueDialog] = useState(false);
  const [queueEmail, setQueueEmail] = useState('');
  const [queueVendor, setQueueVendor] = useState<QuickBooksVendor | null>(null);

  // Form state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedQBVendor, setSelectedQBVendor] = useState<QuickBooksVendor | null>(null);

  // Confirmation dialog state
  const [confirmReplaceDialog, setConfirmReplaceDialog] = useState(false);
  const [existingVendorName, setExistingVendorName] = useState<string>('');
  const [allVendorAssociations, setAllVendorAssociations] = useState<VendorAssociation[]>([]);

  useEffect(() => {
    fetchVendors();
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
    fetchQuickBooksVendors();
    fetchAllVendorAssociations();
    fetchQueueItems();
  }, []);
  
  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        offset: (page * rowsPerPage).toString(),
        limit: rowsPerPage.toString(),
        hasUser: 'true', // Only show vendors with user associations
      });

      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/list?${params.toString()}`
      );

      if (!response || !response.ok) {
        throw new Error('Failed to fetch vendor associations');
      }

      const data = await response.json();
      setVendors(data.data?.vendors || []);
      setTotalCount(data.data?.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching vendor associations:', err);
      setError('Failed to fetch vendor associations');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUsers = async () => {
    try {
      // Fetch all users for vendor associations
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/users`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setUsers(data.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      // Users fetch is not critical, so we don't show an error
    }
  };
  
  const fetchQuickBooksVendors = async () => {
    try {
      // Fetch all QuickBooks vendors (allow multiple users per vendor)
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/vendors/list?limit=500`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch QuickBooks vendors');
      }
      const data = await response.json();
      const allVendors: any[] = data.data?.vendors || [];
      // Map all vendors for the dropdown (allow multiple users per vendor)
      const vendors = allVendors.map((v: any) => ({
        id: v.id,
        displayName: v.displayName,
        companyName: v.companyName,
        active: v.active
      }));
      setQuickBooksVendors(vendors);
    } catch (err) {
      console.error('Error fetching QuickBooks vendors:', err);
      setError('Failed to fetch QuickBooks vendors');
    }
  };

  const fetchAllVendorAssociations = async () => {
    try {
      // Fetch all vendor associations to check for existing user assignments
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/list?hasUser=true&limit=1000`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch all vendor associations');
      }
      const data = await response.json();
      setAllVendorAssociations(data.data?.vendors || []);
    } catch (err) {
      console.error('Error fetching all vendor associations:', err);
      // Not critical, so we don't show an error
    }
  };

  const fetchQueueItems = async () => {
    setLoadingQueue(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/vendor-queue`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch queue items');
      }
      const data = await response.json();
      setQueueItems(data.data || []);
    } catch (err) {
      console.error('Error fetching queue items:', err);
      // Not critical, so we don't show an error
    } finally {
      setLoadingQueue(false);
    }
  };

  const handleCreateQueueItem = async () => {
    if (!queueEmail || !queueVendor) {
      setError('Please enter an email and select a vendor');
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/vendor-queue/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: queueEmail,
          quickbooksVendorId: queueVendor.id,
        }),
      });

      if (!response || !response.ok) {
        const errorData = response ? await response.json() : {};
        throw new Error(errorData.message || 'Failed to create queue item');
      }

      setSuccessMessage('User queued for vendor association successfully');
      setError(null);
      setOpenQueueDialog(false);
      setQueueEmail('');
      setQueueVendor(null);
      fetchQueueItems();
    } catch (err: any) {
      console.error('Error creating queue item:', err);
      setError(err.message || 'Failed to create queue item');
    }
  };

  const handleAddVendor = () => {
    setSelectedVendor(null);
    setSelectedUser(null);
    setSelectedQBVendor(null);
    setOpenDialog(true);
  };
  
  const handleEditVendor = (vendor: VendorAssociation) => {
    setSelectedVendor(vendor);
    const firstUser = vendor.linkedUsers?.[0];
    setSelectedUser(firstUser ? users.find(u => u.id === firstUser.id) || null : null);
    setSelectedQBVendor({
      id: vendor.id,
      displayName: vendor.displayName,
      companyName: vendor.companyName,
      active: vendor.active
    });
    setOpenDialog(true);
  };
  
  const handleDeleteVendor = async (vendor: VendorAssociation) => {
    const firstUser = vendor.linkedUsers?.[0];
    if (!firstUser) {
      setError('No user association to remove');
      return;
    }

    if (!confirm(`Are you sure you want to remove the association between ${firstUser.name} and ${vendor.displayName}?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendor.id}/user/${firstUser.id}`,
        {
          method: 'DELETE',
        }
      );
      
      if (!response || !response.ok) {
        throw new Error('Failed to delete vendor association');
      }
      
      setSuccessMessage('Vendor association removed successfully');
      setError(null);
      fetchVendors();
      fetchQuickBooksVendors(); // Refresh available vendors
      fetchAllVendorAssociations(); // Refresh all associations
    } catch (err) {
      console.error('Error deleting vendor association:', err);
      setError('Failed to remove vendor association');
    }
  };
  
  const handleSaveVendor = async () => {
    if (!selectedUser || !selectedQBVendor) {
      setError('Please select both a user and a vendor');
      return;
    }

    // Check if the selected user already has a vendor association
    const existingAssociation = allVendorAssociations.find(
      (vendor) => vendor.linkedUsers?.some(u => u.id === selectedUser.id)
    );

    if (existingAssociation) {
      // User already has a vendor association, show confirmation dialog
      setExistingVendorName(existingAssociation.displayName);
      setConfirmReplaceDialog(true);
      return;
    }

    // No existing association, proceed with save
    await proceedWithSave();
  };

  const proceedWithSave = async () => {
    if (!selectedUser || !selectedQBVendor) {
      setError('Please select both a user and a vendor');
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/vendors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          quickBooksVendorId: selectedQBVendor.id,
        }),
      });

      if (!response || !response.ok) {
        const errorData = response ? await response.json() : {};
        throw new Error(errorData.message || 'Failed to save vendor association');
      }

      setSuccessMessage('Vendor association created successfully');
      setError(null);
      setOpenDialog(false);
      setConfirmReplaceDialog(false);
      fetchVendors();
      fetchQuickBooksVendors(); // Refresh available vendors
      fetchAllVendorAssociations(); // Refresh all associations
    } catch (err: any) {
      console.error('Error saving vendor association:', err);
      setError(err.message || 'Failed to save vendor association');
    }
  };

  const handleConfirmReplace = () => {
    setConfirmReplaceDialog(false);
    proceedWithSave();
  };
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">User Access Control</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssignmentIndIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            User Access Control
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage user permissions and access to accounting data. Assign portal users to QuickBooks vendors to grant them access to view associated invoices and bills.
        </Typography>
      </Box>
      
      {/* Error and Success Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddVendor}
          >
            Add User-Vendor Association
          </Button>
          <IconButton
            onClick={() => {
              fetchVendors();
              fetchAllVendorAssociations();
              fetchQuickBooksVendors();
            }}
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Paper>
      
      {/* User-Vendor Associations Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : vendors.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No user-vendor associations found. Click "Add User-Vendor Association" to create one.
          </Alert>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Assigned User</TableCell>
                  <TableCell>QuickBooks Vendor</TableCell>
                  <TableCell>User Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendors.flatMap((vendor) => {
                  // Show a row for each linked user
                  if (!vendor.linkedUsers || vendor.linkedUsers.length === 0) {
                    return (
                      <TableRow key={`${vendor.id}-no-user`}>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            Not assigned
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {vendor.displayName}
                          </Typography>
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>
                          <Chip
                            label={vendor.active ? 'Active' : 'Inactive'}
                            color={vendor.active ? 'success' : 'default'}
                            size="small"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    );
                  }

                  return vendor.linkedUsers.map((user) => (
                    <TableRow key={`${vendor.id}-${user.id}`}>
                      <TableCell>
                        <Chip
                          label={user.name}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {vendor.displayName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vendor.active ? 'Active' : 'Inactive'}
                          color={vendor.active ? 'success' : 'default'}
                          size="small"
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Create a vendor object with only this specific user for deletion
                            const vendorWithSingleUser = {
                              ...vendor,
                              linkedUsers: [user]
                            };
                            handleDeleteVendor(vendorWithSingleUser);
                          }}
                          title="Remove User Association"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>

      {/* Vendor Association Queue */}
      <Box sx={{ mt: 6, mb: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Vendor Association Queue
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Queue users for vendor association. These will be processed when the user logs in.
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenQueueDialog(true)}
        >
          Queue New User for Vendor Association
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        {loadingQueue ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : queueItems.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No queued vendor associations found.
          </Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor Name</TableCell>
                <TableCell>User Email</TableCell>
                <TableCell>Complete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queueItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {item.vendorName}
                    </Typography>
                    {item.vendorCompany && (
                      <Typography variant="caption" color="text.secondary">
                        {item.vendorCompany}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.userEmail}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.complete ? 'Yes' : 'No'}
                      color={item.complete ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setConfirmReplaceDialog(false);
          setExistingVendorName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add User-Vendor Association
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Autocomplete
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              options={users}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="User"
                  required
                  helperText="Select a user to associate with this vendor"
                />
              )}
              disabled={!!selectedVendor}
            />
            <Autocomplete
              value={selectedQBVendor}
              onChange={(_, newValue) => setSelectedQBVendor(newValue)}
              options={quickBooksVendors.filter(v => v.active)}
              getOptionLabel={(option) => option.displayName}
              renderInput={(params) => (
                <TextField {...params} label="QuickBooks Vendor" required />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setConfirmReplaceDialog(false);
              setExistingVendorName('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveVendor} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Replacing Existing Association */}
      <Dialog
        open={confirmReplaceDialog}
        onClose={() => setConfirmReplaceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Replace Existing Vendor Association?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Warning: This user already has an existing vendor association.
          </Alert>
          <Typography variant="body1" gutterBottom>
            <strong>{selectedUser?.name}</strong> is currently associated with:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            • <strong>{existingVendorName}</strong>
          </Typography>
          <Typography variant="body1">
            This existing association will be <strong>deleted</strong> and replaced with:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            • <strong>{selectedQBVendor?.displayName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The user will lose access to all data from {existingVendorName}
            and will gain access to data from {selectedQBVendor?.displayName} instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReplaceDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReplace}
            variant="contained"
            color="warning"
          >
            Replace Association
          </Button>
        </DialogActions>
      </Dialog>

      {/* Queue New User Dialog */}
      <Dialog
        open={openQueueDialog}
        onClose={() => {
          setOpenQueueDialog(false);
          setQueueEmail('');
          setQueueVendor(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Queue New User for Vendor Association</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="User Email"
              type="email"
              value={queueEmail}
              onChange={(e) => setQueueEmail(e.target.value)}
              required
              fullWidth
              helperText="Enter the email address of the user to associate with a vendor"
            />
            <Autocomplete
              value={queueVendor}
              onChange={(_, newValue) => setQueueVendor(newValue)}
              options={quickBooksVendors.filter(v => v.active)}
              getOptionLabel={(option) => option.displayName}
              renderInput={(params) => (
                <TextField {...params} label="QuickBooks Vendor" required />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenQueueDialog(false);
              setQueueEmail('');
              setQueueVendor(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateQueueItem} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};